import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Textarea,
  Text,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { useCancelBookingDialogStyles } from './CancelBookingDialog.styles';

const MAX_REASON_LENGTH = 500;

export interface CancelBookingDialogProps {
  open: boolean;
  eventTitle: string;
  isProcessing: boolean;
  error: string | null;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export const CancelBookingDialog = ({
  open,
  eventTitle,
  isProcessing,
  error,
  onConfirm,
  onCancel,
}: CancelBookingDialogProps) => {
  const styles = useCancelBookingDialogStyles();
  const { t } = useTranslation('planner');
  const [reason, setReason] = useState('');
  const [validationError, setValidationError] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason('');
      setValidationError(false);
    }
  }, [open]);

  const handleSubmit = useCallback(() => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setValidationError(true);
      return;
    }
    setValidationError(false);
    onConfirm(trimmed);
  }, [reason, onConfirm]);

  const handleCancel = useCallback(() => {
    setReason('');
    setValidationError(false);
    onCancel();
  }, [onCancel]);

  const handleReasonChange = useCallback(
    (_e: unknown, data: { value: string }) => {
      const value = data.value.slice(0, MAX_REASON_LENGTH);
      setReason(value);
      if (validationError && value.trim()) {
        setValidationError(false);
      }
    },
    [validationError]
  );

  return (
    <Dialog
      open={open}
      modalType="modal"
      onOpenChange={(_, data) => {
        if (!data.open && !isProcessing) {
          handleCancel();
        }
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle
            action={
              <BasicButton
                variant="subtle"
                size="medium"
                aria-label={t('cancelBookingDialog.close')}
                icon={<Dismiss24Regular />}
                onClick={handleCancel}
                disabled={isProcessing}
              />
            }
          >
            {t('cancelBookingDialog.title')}
          </DialogTitle>
          <DialogContent>
            <p className={styles.bodyText}>{t('cancelBookingDialog.bodyMessage')}</p>
            <p className={styles.confirmQuestion}>
              {t('cancelBookingDialog.confirmQuestion')}
              <br />
              <strong>{eventTitle}</strong>
            </p>
            <div className={styles.reasonSection}>
              <Text as="span" weight="semibold" className={styles.label}>
                {t('cancelBookingDialog.reasonLabel')} <span className={styles.required}>*</span>
              </Text>
              <Textarea
                className={styles.textarea}
                placeholder={t('cancelBookingDialog.reasonPlaceholder')}
                value={reason}
                onChange={handleReasonChange}
                disabled={isProcessing}
                resize="vertical"
                aria-label={t('cancelBookingDialog.reasonLabel')}
                data-testid="cancel-booking-reason-textarea"
              />
              <Text className={styles.charCount}>
                {reason.length}/{MAX_REASON_LENGTH}
              </Text>
            </div>
            {validationError && (
              <Text className={styles.errorText} data-testid="cancel-booking-validation-error">
                {t('cancelBookingDialog.reasonRequired')}
              </Text>
            )}
            {error && (
              <Text className={styles.errorText} data-testid="cancel-booking-api-error">
                {t('dialogs.error')}
              </Text>
            )}
          </DialogContent>
          <DialogActions>
            <BasicButton
              variant="secondary"
              size="medium"
              onClick={handleCancel}
              disabled={isProcessing}
              data-testid="cancel-booking-dismiss-button"
            >
              {t('cancelBookingDialog.dismissButton')}
            </BasicButton>
            <BasicButton
              variant="primary"
              size="medium"
              onClick={handleSubmit}
              disabled={isProcessing}
              data-testid="cancel-booking-confirm-button"
            >
              {isProcessing
                ? t('dialogs.claim.processing')
                : t('cancelBookingDialog.confirmButton')}
            </BasicButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

CancelBookingDialog.displayName = 'CancelBookingDialog';
